// Field editability and editor type configuration

const NON_EDITABLE_FIELDS = new Set([
  'key',
  'created',
  'updated',
  'issuetype',
  'project',
  'projectCategory', // a project attribute — not settable from a work item
  'timespent',
]);

const EDITOR_TYPE_MAP = {
  summary: 'text',
  startDate: 'date',
  duedate: 'date',
  priority: 'select',
  resolution: 'select',
  status: 'status',
  assignee: 'user',
  reporter: 'user',
  labels: 'labels',
  components: 'multiselect',
  fixVersions: 'multiselect',
  timeoriginalestimate: 'time',
  timeestimate: 'time',
  parent: 'parent',
};

/**
 * Returns true if the field/column can be inline-edited.
 */
export function isFieldEditable(fieldId, column) {
  if (!fieldId) return false;
  if (column?.type === 'formula') return false;
  if (NON_EDITABLE_FIELDS.has(fieldId)) return false;
  return true;
}

/**
 * Determines which editor widget to render for the given field.
 * Falls back to jiraFields schema for custom fields.
 */
export function getEditorType(fieldId, jiraFields) {
  if (EDITOR_TYPE_MAP[fieldId]) return EDITOR_TYPE_MAP[fieldId];

  // Custom fields — inspect schema from Jira field metadata
  if (fieldId?.startsWith('customfield_') && Array.isArray(jiraFields)) {
    const meta = jiraFields.find(f => f.id === fieldId);
    if (meta?.schema) {
      const { type, custom } = meta.schema;
      // schema.custom can reveal a date-flavoured field even when type is
      // 'string' or 'number' — e.g. Plans/Advanced Roadmaps' Start/End date
      // (com.atlassian.jpo:jpo-custom-field-baseline-start/end) report
      // type:'string' but Jira's backend rejects '' on save with
      // "must be of the format yyyy-MM-dd". Recognise these before the
      // generic type checks.
      if (typeof custom === 'string' && /date|baseline-(start|end)/i.test(custom)) return 'date';
      if (type === 'date') return 'date';
      if (type === 'datetime') return 'date';
      if (type === 'number') return 'number';
      if (type === 'string') return 'text';
      if (type === 'array' && meta.schema.items === 'option') return 'multiselect';
      if (type === 'option' || custom?.includes('select')) return 'select';
      if (type === 'user' || custom?.includes('userpicker')) return 'user';
      if (type === 'array' && meta.schema.items === 'user') return 'user';
    }
    // Default for unknown custom fields
    return 'text';
  }

  // Unknown standard field — default to text
  return 'text';
}

/**
 * Extracts the raw Jira value (not display-formatted) from an issue.
 */
export function getRawFieldValue(issue, fieldId) {
  if (!issue) return null;
  if (fieldId === 'key') return issue.key;
  if (fieldId === 'startDate') {
    return issue.fields?.startDate || issue.fields?.customfield_10015 || null;
  }
  return issue.fields?.[fieldId] ?? null;
}

/**
 * Human-readable label for the popover header.
 */
export function getFieldLabel(fieldId, columns) {
  if (Array.isArray(columns)) {
    const col = columns.find(c => c.fieldId === fieldId);
    if (col?.name) return col.name;
  }
  // Fallback: capitalize fieldId
  const labels = {
    summary: 'Summary',
    startDate: 'Start Date',
    duedate: 'Due Date',
    priority: 'Priority',
    resolution: 'Resolution',
    status: 'Status',
    assignee: 'Assignee',
    reporter: 'Reporter',
    labels: 'Labels',
    components: 'Components',
    fixVersions: 'Fix Versions',
    timeoriginalestimate: 'Original Estimate',
    timeestimate: 'Remaining Estimate',
    timespent: 'Time Spent',
  };
  return labels[fieldId] || fieldId;
}
